import { Rocket, Mail, Users, BarChart3, Megaphone, Workflow, Calendar, MessageSquare, Bell, Puzzle } from 'lucide-react'

const roadmap = [
  {
    icon: Megaphone,
    title: 'Email Marketing Campaigns',
    desc: 'Design, schedule, and send bulk email campaigns with advanced segmentation, A/B testing, and real-time analytics. Track opens, clicks, bounces, and conversions.',
    eta: 'Q3 2026',
    status: 'planned',
  },
  {
    icon: Users,
    title: 'Complete CRM Module',
    desc: 'Full customer relationship management — contacts, companies, deals pipeline, activity tracking, notes, tasks, and email integration all in one place.',
    eta: 'Q4 2026',
    status: 'planned',
  },
  {
    icon: Workflow,
    title: 'Marketing Automation',
    desc: 'Visual workflow builder for automated drip sequences, lead nurturing, behavioral triggers, and multi-channel outreach.',
    eta: 'Q1 2027',
    status: 'planned',
  },
  {
    icon: BarChart3,
    title: 'Advanced Analytics & Reports',
    desc: 'Custom dashboards, scheduled reports, exportable PDF/CSV reports, campaign ROI tracking, and audience insights.',
    eta: 'Q1 2027',
    status: 'planned',
  },
  {
    icon: Mail,
    title: 'Inbox Warmup & Reputation',
    desc: 'Automated inbox warmup for new domains, domain reputation monitoring, blacklist checking, and deliverability scoring.',
    eta: 'Q2 2027',
    status: 'planned',
  },
  {
    icon: Calendar,
    title: 'Smart Scheduling',
    desc: 'AI-powered send-time optimization, timezone-aware delivery, recurring campaigns, and calendar-based campaign planning.',
    eta: 'Q2 2027',
    status: 'planned',
  },
  {
    icon: MessageSquare,
    title: 'Multi-Channel Outreach',
    desc: 'SMS, WhatsApp, and social DM integration — manage all outreach channels from a single unified inbox.',
    eta: 'Q3 2027',
    status: 'planned',
  },
  {
    icon: Bell,
    title: 'Real-Time Notifications',
    desc: 'Push, email, and webhook notifications for campaign milestones, lead activity, and system alerts.',
    eta: 'Q3 2027',
    status: 'planned',
  },
  {
    icon: Puzzle,
    title: 'Integrations Marketplace',
    desc: 'Native integrations with major CRMs, e-commerce platforms, payment gateways, and developer APIs for custom workflows.',
    eta: 'Q4 2027',
    status: 'planned',
  },
]

const statusColors = {
  planned: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
}

export default function Modules() {
  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-purple-100 dark:bg-purple-900/30 mb-2">
          <Rocket className="h-8 w-8 text-purple-600 dark:text-purple-400" />
        </div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
          Modules &amp; Extensions
        </h1>
        <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto text-lg">
          We're building a complete marketing platform on top of our industry-leading email verification engine. Here's what's coming.
        </p>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <Rocket className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">Coming Soon</span>
        </div>
      </div>

      {/* Roadmap Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {roadmap.map((item) => (
          <div
            key={item.title}
            className="relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 hover:shadow-lg hover:border-purple-200 dark:hover:border-purple-800 transition-all duration-300 group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 dark:bg-purple-900/20 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/30 transition-colors">
                <item.icon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full border ${statusColors[item.status]}`}>
                {item.eta}
              </span>
            </div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* Bottom CTA */}
      <div className="text-center pb-8">
        <p className="text-sm text-slate-400 dark:text-slate-500">
          Have a feature request? We'd love to hear from you.{' '}
          <a href="https://smartemailverifier.com/feedback" target="_blank" rel="noopener noreferrer" className="text-brand-600 dark:text-brand-400 hover:underline font-medium">
            Reach out via the contact form
          </a>{' '}
          or email us directly.
        </p>
      </div>
    </div>
  )
}
